import type { BlockDefinition, VariableMeta } from './block'
import { BlockDefinitionSchema } from './block'
import { parseLegacyBlockVersion } from './version'

const BLOCK_DEFAULT_WIDTH_MM = 80
const BLOCK_DEFAULT_HEIGHT_MM = 40
const SINGLE_PLACEHOLDER_RE = /\{\{([a-zA-Z][a-zA-Z0-9_]*)\}\}/

type LegacyInput = {
  kind: 'field' | 'text' | 'number' | 'format' | 'enum' | 'boolean'
  fieldKey?: string
  default?: string | number | boolean
  options?: string[]
  label?: string
  required?: boolean
}

function legacyInputToVariableMeta(input: LegacyInput): VariableMeta {
  switch (input.kind) {
    case 'field':
      return { type: 'text', default: input.fieldKey ?? '' }
    case 'text':
    case 'format':
      return { type: 'text', default: (input.default as string) ?? '' }
    case 'number':
      return {
        type: 'number',
        default: input.default !== undefined ? String(input.default) : '',
      }
    case 'enum':
      return {
        type: 'enum',
        default: (input.default as string) ?? input.options?.[0] ?? '',
        enumOptions: input.options,
      }
    case 'boolean':
      return {
        type: 'boolean',
        default: input.default ? 'true' : 'false',
      }
    default:
      return { type: 'text', default: '' }
  }
}

/**
 * Migrates a legacy ValueSource (static/bound) to the new TextValue shape.
 * If the value already has `content`, it's returned as-is (idempotent path).
 * Side effect: may synthesize an entry in `variables` when a direct binding
 * is encountered that isn't yet declared.
 */
function migrateValueSource(
  source: unknown,
  variables: Record<string, VariableMeta>
): { content: string } | undefined {
  if (source === undefined || source === null) return undefined
  if (typeof source !== 'object') return undefined
  const s = source as Record<string, unknown>
  if (typeof s.content === 'string') {
    return { content: s.content } // already migrated
  }
  if (s.kind === 'static' && typeof s.value === 'string') {
    const m = SINGLE_PLACEHOLDER_RE.exec(s.value)
    if (!m) return { content: s.value }
    // Mixed content (or exact placeholder): collapse to the single variable.
    // This matches the legacy runtime, which silently dropped surrounding text.
    return { content: `{${m[1]}}` }
  }
  if (s.kind === 'bound' && typeof s.fieldKey === 'string') {
    const fk = s.fieldKey
    const m = SINGLE_PLACEHOLDER_RE.exec(fk)
    if (m) return { content: `{${m[1]}}` }
    // Direct binding to a real field key — synthesize a variable entry.
    if (!variables[fk]) variables[fk] = { type: 'text', default: '' }
    return { content: `{${fk}}` }
  }
  return undefined
}

/**
 * Rewrites any legacy ValueSource shapes inside a single leaf component.
 * Returns a NEW component object when something was rewritten, or
 * `undefined` when the component was already canonical. Shared by the
 * block-side `migrateLayout` and the template-side self-heal
 * (`migrateTemplateRow` in the demo adapter).
 */
export function migrateComponentValueSources(
  component: Record<string, unknown>,
  variables: Record<string, VariableMeta>
): Record<string, unknown> | undefined {
  const isLegacy = (v: unknown): boolean =>
    typeof v !== 'object' || v === null || !('content' in (v as Record<string, unknown>))
  const c: any = { ...component }
  let changed = false

  if (c.type === 'text' && c.value !== undefined && isLegacy(c.value)) {
    c.value = migrateValueSource(c.value, variables)
    changed = true
  } else if (
    (c.type === 'localized-text' || c.type === 'localized-rich-text') &&
    Array.isArray(c.variants) &&
    c.variants.some((v: any) => isLegacy(v.value))
  ) {
    c.variants = c.variants.map((v: any) =>
      isLegacy(v.value) ? { ...v, value: migrateValueSource(v.value, variables) } : v
    )
    changed = true
  } else if (c.type === 'symbol' && c.dataStructure) {
    const ds = { ...c.dataStructure }
    if ((ds.kind === 'raw' || ds.kind === 'gtin' || ds.kind === 'url') && isLegacy(ds.value)) {
      ds.value = migrateValueSource(ds.value, variables)
      c.dataStructure = ds
      changed = true
    } else if (ds.kind === 'gs1-ai' && Array.isArray(ds.elements) && ds.elements.some((el: any) => isLegacy(el.value))) {
      ds.elements = ds.elements.map((el: any) =>
        isLegacy(el.value) ? { ...el, value: migrateValueSource(el.value, variables) } : el
      )
      c.dataStructure = ds
      changed = true
    }
  } else if (c.type === 'image' && c.source && (c.source as any).kind === 'field') {
    const s: any = { ...c.source }
    if (typeof s.fieldKey === 'string') {
      const m = SINGLE_PLACEHOLDER_RE.exec(s.fieldKey)
      if (m) {
        s.fieldKey = m[1]
        c.source = s
        changed = true
      } else if (!variables[s.fieldKey]) {
        variables[s.fieldKey] = { type: 'text', default: '' }
        // variable synthesis only — component itself unchanged
      }
    }
  }

  return changed ? c : undefined
}

const ALIGNMENT_TO_HALIGN: Record<string, 'left' | 'center' | 'right' | 'justify'> = {
  left: 'left',
  center: 'center',
  right: 'right',
  justify: 'justify',
}

/**
 * If a component carries a legacy `alignment` field, fold it into `placement`:
 *
 *   - `placement` already exists → drop `alignment` silently (placement wins).
 *   - only `alignment` exists    → set `placement: { hAlign: <translated>, vAlign: 'top' }`.
 *   - neither exists             → return component as-is.
 *
 * Default vAlign of 'top' preserves the visual behavior of pre-placement
 * blocks (text sits at zone top, anchor at zone.x/y). Idempotent: a second
 * call on already-migrated input is a no-op because `alignment` is gone.
 */
export function foldAlignmentIntoPlacement<T extends Record<string, unknown>>(component: T): T {
  if (!('alignment' in component)) return component
  const rest: Record<string, unknown> = { ...component }
  const legacy = rest.alignment as string | undefined
  delete rest.alignment
  if ('placement' in rest && rest.placement) return rest as T
  if (legacy === undefined) return rest as T
  const hAlign = ALIGNMENT_TO_HALIGN[legacy]
  if (!hAlign) return rest as T
  rest.placement = { hAlign, vAlign: 'top' }
  return rest as T
}

function migrateLayout(
  zone: any,
  variables: Record<string, VariableMeta>
): any {
  if (!zone || typeof zone !== 'object') return zone
  const out: any = { ...zone }

  if (out.component && typeof out.component === 'object') {
    const migrated = migrateComponentValueSources(out.component as Record<string, unknown>, variables)
    const c = migrated ?? { ...(out.component as Record<string, unknown>) }
    out.component = foldAlignmentIntoPlacement(c)
  }

  if (Array.isArray(out.children) && out.children.length === 2) {
    out.children = [
      migrateLayout(out.children[0], variables),
      migrateLayout(out.children[1], variables),
    ]
  }

  return out
}

/**
 * Idempotent migration of any block-shaped record (legacy or already-current)
 * to the current BlockDefinition schema. Invoked from every adapter's
 * loadBlock/loadTemplate read path before the Zod parse.
 *
 * Migration steps:
 *   1. Fill widthMm/heightMm with 80×40 defaults for legacy records.
 *   2. Convert legacy `inputs` map to a `variables` map (preserving names,
 *      types, defaults, enum options).
 *   3. Walk the layout, rewriting `ValueSource` legacy shapes:
 *        { kind: 'static', value }            → { content }
 *        { kind: 'static', value: '{{x}}' }   → { content: '{x}' }
 *        { kind: 'bound', fieldKey: '{{x}}' } → { content: '{x}' }
 *        { kind: 'bound', fieldKey: 'realKey' } → { content: '{realKey}' }
 *          and synthesize variables.realKey = { type: 'text', default: '' }
 *   4. Drop the legacy `inputs` field.
 *   5. Normalize the version to a positive integer.
 *
 * SeparatorComponent primitives are preserved unchanged (deprecated path).
 */
export function migrateBlockDefinition(input: unknown): BlockDefinition {
  const candidate =
    typeof input === 'object' && input !== null
      ? { ...(input as Record<string, unknown>) }
      : {}

  const variables: Record<string, VariableMeta> = {
    ...(candidate.variables as Record<string, VariableMeta> | undefined),
  }

  if (candidate.inputs && typeof candidate.inputs === 'object') {
    for (const [name, input] of Object.entries(
      candidate.inputs as Record<string, LegacyInput>
    )) {
      if (!variables[name]) {
        variables[name] = legacyInputToVariableMeta(input)
      }
    }
  }
  delete candidate.inputs

  if (candidate.layout) {
    candidate.layout = migrateLayout(candidate.layout, variables)
  }

  const patched = {
    ...candidate,
    widthMm: candidate.widthMm ?? BLOCK_DEFAULT_WIDTH_MM,
    heightMm: candidate.heightMm ?? BLOCK_DEFAULT_HEIGHT_MM,
    version: parseLegacyBlockVersion(candidate.version),
    variables: Object.keys(variables).length > 0 ? variables : undefined,
  }

  return BlockDefinitionSchema.parse(patched)
}

/** @deprecated use migrateBlockDefinition. */
export function withBlockDimensionDefaults(input: unknown): BlockDefinition {
  return migrateBlockDefinition(input)
}
